import { pickLbApiBase } from "./pickLbApi";

const commerceApiUrl = String(
  (import.meta.env.DEV
    ? import.meta.env.VITE_DEV_COMMERCE_API_URL
    : import.meta.env.VITE_STABLE_COMMERCE_API_URL) ||
    import.meta.env.VITE_COMMERCE_API_URL ||
    "http://127.0.0.1:8791"
).replace(/\/$/, "");

async function responseJson(response, fallback) {
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(body.message || body.error || fallback);
    error.status = response.status;
    error.code = body.code || body.error;
    error.payload = body.data || null;
    throw error;
  }
  return body;
}

export async function getStorefrontTheme(storeSlug, { signal } = {}) {
  if (!storeSlug) return null;
  const response = await fetch(
    `${commerceApiUrl}/v1/storefront/stores/${encodeURIComponent(
      storeSlug
    )}/home-page`,
    { headers: { Accept: "application/json" }, signal }
  );
  const body = await responseJson(
    response,
    "Não foi possível carregar o tema da loja."
  );
  const settings = body.data?.page?.content?.settings;
  if (!settings) return null;
  const hasTheme = Boolean(settings.themeId);
  const hasColors = Boolean(
    settings.primaryColor ||
      settings.backgroundColor ||
      settings.buttonColor ||
      settings.textColor
  );
  if (!hasTheme && !hasColors) return null;
  return {
    themeId: settings.themeId || "custom",
    themeDark: Boolean(settings.themeDark),
    backgroundColor: settings.backgroundColor || "#ffffff",
    textColor: settings.textColor || "#111827",
    primaryColor: settings.primaryColor || "#111827",
    buttonColor: settings.buttonColor || settings.primaryColor || "#111827",
    buttonTextColor: settings.buttonTextColor || "#ffffff",
    mutedColor: settings.mutedColor || "#6b7280",
    primaryGradientStart: settings.primaryGradientStart || "",
    primaryGradientEnd: settings.primaryGradientEnd || "",
  };
}

export async function getCommerceCheckout(checkoutToken, { signal } = {}) {
  const response = await fetch(
    `${commerceApiUrl}/v1/storefront/checkout/${encodeURIComponent(
      checkoutToken
    )}`,
    { headers: { Accept: "application/json" }, signal }
  );
  const body = await responseJson(
    response,
    "Não foi possível carregar o checkout."
  );
  return body.data;
}

export async function prepareCommerceCheckout(
  storeSlug,
  items,
  { signal } = {}
) {
  const response = await fetch(
    `${commerceApiUrl}/v1/storefront/stores/${encodeURIComponent(
      storeSlug
    )}/checkout/prepare`,
    {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "Idempotency-Key": crypto.randomUUID(),
      },
      body: JSON.stringify({
        items: items.map((item) => ({
          variantId: item.variantId,
          quantity: Number(item.quantity),
        })),
      }),
      signal,
    }
  );
  const body = await responseJson(
    response,
    "Não foi possível renovar o checkout."
  );
  return body.data;
}

export async function saveCommerceCheckoutCustomer(
  checkoutToken,
  customer,
  { signal } = {}
) {
  const response = await fetch(
    `${commerceApiUrl}/v1/storefront/checkout/${encodeURIComponent(
      checkoutToken
    )}/customer`,
    {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ customer }),
      signal,
    }
  );
  return responseJson(response, "Não foi possível salvar seus dados.");
}

export async function lookupCep(cep, { signal } = {}) {
  const digits = String(cep || "").replace(/\D/g, "");
  const response = await fetch(
    `${commerceApiUrl}/v1/storefront/cep/${encodeURIComponent(digits)}`,
    { headers: { Accept: "application/json" }, signal }
  );
  return responseJson(response, "Não foi possível consultar o CEP.");
}

export async function initiateCommercePayment(payload, { signal } = {}) {
  const response = await fetch(
    `${String(pickLbApiBase()).replace(/\/$/, "")}/commerce/order/initiate`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      signal,
    }
  );
  return responseJson(response, "Não foi possível processar o pagamento.");
}

export async function tokenizeCommerceCard(card) {
  const publicKey =
    import.meta.env.VITE_PAGARME_PUBLIC_KEY || "pk_dzr5yBqtgQFvM7k0";
  const response = await fetch(
    `https://api.pagar.me/core/v5/tokens?appId=${encodeURIComponent(
      publicKey
    )}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        type: "card",
        card: {
          number: card.number.replace(/\D/g, ""),
          holder_name: card.holderName.trim(),
          exp_month: card.expDate.split("/")[0],
          exp_year: card.expDate.split("/")[1],
          cvv: card.cvv,
        },
      }),
    }
  );
  const body = await responseJson(
    response,
    "Verifique os dados do cartão e tente novamente."
  );
  if (!body.id) throw new Error("Não foi possível tokenizar o cartão.");
  return body.id;
}
